import React, { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, Outlet, useParams, useLocation } from 'react-router-dom';
import {
  CLASS_MATERIALS_BASE,
  CLASS_MATERIALS_MANAGE_BASE,
  CONTENT_MANAGER_ROLES,
  TEACHER_TRAINING_BASE,
  TEACHER_TRAINING_LOBBY_PATH,
  TEACHER_TRAINING_MANAGE_BASE,
} from './config/contentRoutes';
import ClassMaterialsManage from './pages/class-materials/manage/ClassMaterialsManage';
import TeacherTrainingManage from './pages/teacher-training/manage/TeacherTrainingManage';
import AIEdutechTrainingManage from './pages/teacher-training/manage/AIEdutechTrainingManage';
import TrainingHostDirectory from './pages/teacher-training/TrainingHostDirectory';
import TrainingHostPublicPage from './pages/teacher-training/TrainingHostPublicPage';
import TrainingLobbyPage from './pages/teacher-training/TrainingLobbyPage';
import TrainingProgramEditor from './pages/teacher-training/manage/TrainingProgramEditor';
import TrainingProgramRegister from './pages/teacher-training/manage/TrainingProgramRegister';
import TrainingProgramPublicPage from './pages/teacher-training/TrainingProgramPublicPage';
/** 예전 /notes/* 경로를 새 허브 경로로 리다이렉트 */
const LegacyContentPathRedirect: React.FC<{
  legacyPrefix: string;
  targetPrefix: string;
  defaultSuffix?: string;
}> = ({ legacyPrefix, targetPrefix, defaultSuffix = '' }) => {
  const location = useLocation();
  const rest = location.pathname.replace(new RegExp(`^${legacyPrefix}`), '') || defaultSuffix;
  const target = `${targetPrefix}${rest}${location.search}${location.hash}`;
  return <Navigate to={target} replace />;
};
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import PlatformProtectedRoute from './components/PlatformProtectedRoute';
import { Teacher, User, isTeacher, app } from './utils/firebase';
import { initializeTeams, isInTeams } from './utils/teams';
import Login from './pages/auth/Login';
import TeacherRegister from './pages/auth/TeacherRegister';
import StudentRegister from './pages/auth/StudentRegister';
import SignupChoice from './pages/auth/SignupChoice';
import Home from './pages/Home';
import MaruPolicy from './pages/MaruPolicy';
import ClassMain from './pages/class/main/ClassManagementHome';
import ClassManagementHome from './pages/class/main/ClassManagementHome';
import ClassManagement from './pages/class/teacher/ClassManagement';
import ClassMaterials from './pages/class/teacher/ClassMaterials';
import StoreManagement from './pages/class/teacher/StoreManagement';
import ScheduleManagement from './pages/class/teacher/ScheduleManagement';
import SeatManagement from './pages/class/teacher/SeatManagement';
import SeatDisplay from './pages/class/teacher/SeatDisplay';
import AttendanceManagement from './pages/class/teacher/AttendanceManagement';
import VirtualPetManagement from './pages/class/teacher/VirtualPetManagement';
import MyHomeModeration from './pages/class/teacher/MyHomeModeration';
import KioskConnect from './pages/class/kiosk/KioskConnect';
import TeacherSetting from './pages/class/teacher/TeacherSetting';
import TeacherPage from './pages/class/teacher/ClassTeacherHome';
import ClassRoleManagement from './pages/class/teacher/ClassRoleManagement';
import PointApprovalManagement from './pages/class/teacher/point-approval/PointApprovalManagement';
import ClassPointManagement from './pages/class/teacher/class-point/ClassPointManagement';
import StampPage from './pages/subject/teacher/StampPage';
import NumberDraw from './pages/class/teacher/NumberDraw';
import Lottery from './pages/class/teacher/Lottery';
import TeacherProfile from './pages/class/teacher/TeacherProfile';
import PublicTeacherProfile from './pages/public/PublicTeacherProfile';
import ItemScanner from './pages/class/teacher/ItemScanner';
import ItemApproval from './pages/class/teacher/ItemApproval';
import StudentClassPage from './pages/class/student/StudentClassPage';
import ClassStudentHome from './pages/class/student/ClassStudentHome';
import StudentManito from './pages/class/student/StudentManito';
import StudentBirthdayRollingPaper from './pages/class/student/StudentBirthdayRollingPaper';

import StudentClassPoints from './pages/class/student/StudentClassPoints';
import StudentAchievements from './pages/class/student/StudentAchievements';
import StudentStamp from './pages/class/student/StudentStamp';
import ClassItemShop from './pages/class/student/ClassItemShop';
import { FEATURE_CHARACTER_COSMETIC } from './config/featureFlags';
const CharacterWardrobeLazy = lazy(() => import('./pages/class/student/CharacterWardrobe'));
import SeatReservation from './pages/class/student/SeatReservation';
import ClassMyItems from './pages/class/student/ClassMyItems';
import TeacherApproval from './pages/class/admin/TeacherApproval';
import SchoolManagement from './pages/class/admin/SchoolManagement';
import EventManagement from './pages/class/teacher/EventManagement';
import StudentSettings from './pages/class/student/StudentSettings';
import MessageInbox from './pages/class/MessageInbox';
import StudentPointApproval from './pages/class/student/StudentPointApproval';
import GameRegister from './pages/makecodearcade/GameRegister';
import ArcadeMain from './pages/makecodearcade/ArcadeMain';
import Store from './pages/makecodearcade/Store';
import GameDetail from './pages/makecodearcade/GameDetail';
import GameChart from './pages/makecodearcade/GameChart';
import GameClassManagement from './pages/makecodearcade/GameManagement';
import GameStudentManagement from './pages/makecodearcade/GameStudentManagement';
import TrainingMainPage from './pages/notes/teacher/TrainingMainPage';
import TrainingPages from './pages/notes/teacher/TrainingPages';
import MakeCodeArcadeTraining from './pages/notes/teacher/teacher-training/completed/MakeCodeArcadeTraining';
import ThreeDModelingTraining from './pages/notes/teacher/teacher-training/upcoming/ThreeDModelingTraining';
import DeaGwamgAIEdutechTraining from './pages/notes/teacher/AIEdutech/DeaGwamgAIEdutechTraining';
import Elementary3DPrintingTraining2026 from './pages/notes/teacher/AIEdutech/Elementary3DPrintingTraining2026';
import MiddleSchool3DPrintingTraining2026 from './pages/notes/teacher/AIEdutech/MiddleSchool3DPrintingTraining2026';
import CentralMiddleDebethAIDigitalLeaderTraining2026 from './pages/notes/teacher/AIEdutech/CentralMiddleDebethAIDigitalLeaderTraining2026';
import NamdaemoonMiddleSchoolDebethTraining2026 from './pages/notes/teacher/AIEdutech/NamdaemoonMiddleSchoolDebethTraining2026';
import NamdaemoonMiddleSchoolDebethTraining2026Session2 from './pages/notes/teacher/AIEdutech/NamdaemoonMiddleSchoolDebethTraining2026Session2';
import EwhaGeumranMiddleSchoolAITraining2026 from './pages/notes/teacher/AIEdutech/EwhaGeumranMiddleSchoolAITraining2026';
import JingwanMiddleSchoolAITraining2026 from './pages/notes/teacher/AIEdutech/JingwanMiddleSchoolAITraining2026';
import SetiVibeCodingTraining from './pages/notes/teacher/AIEdutech/SetiVibeCodingTraining';
import RobotHighSchoolVibeCodingTraining from './pages/notes/teacher/AIEdutech/RobotHighSchoolVibeCodingTraining';
import SunrinAITraining from './pages/notes/teacher/AIEdutech/SunrinAITraining';
import AIEdutechMainpage from './pages/notes/teacher/AIEdutech/AIEdutechMainpage';
import EvaluationCodeGenerator from './pages/makecodearcade/EvaluationCodeGenerator';
import UploadCodePage from './pages/makecodearcade/UploadCodePage';
import RegisterGuide from './pages/makecodearcade/RegisterGuide';
import Extensions from './pages/makecodearcade/extensions';
import TilemapExplain from './pages/makecodearcade/explains/tilemap';
import TextSpriteExplain from './pages/makecodearcade/explains/TextSprite';
import Explains from './pages/makecodearcade/explains';
import WordGame from './pages/class/teacher/WordGame';
import ImageConverter from './pages/makecodearcade/extensions/imageConverter';
import TilemapExtension from './pages/makecodearcade/extensions/tilemap';
import TextSpriteExtension from './pages/makecodearcade/extensions/TextSprite';
import StoryExplain from './pages/makecodearcade/explains/Story';
import ScreenScrollExplain from './pages/makecodearcade/explains/ScreenScroll';
import AchievementManagement from './pages/class/teacher/AchievementManagement';
import { ImageGallery } from './pages/makecodearcade/ImageGallery';
import AssetPackShare from './pages/makecodearcade/AssetPackShare';
import { UploadImage } from './pages/UploadImage';
import SeatReservationManagement from './pages/class/teacher/SeatReservationManagement';
import PointApprovalManagementDaily from './pages/class/teacher/point-approval/PointApprovalManagementDaily';
import PointApprovalManagementWeekly from './pages/class/teacher/point-approval/PointApprovalManagementWeekly';
import PointApprovalManagementMonthly from './pages/class/teacher/point-approval/PointApprovalManagementMonthly';
import PointApprovalManagementTax from './pages/class/teacher/point-approval/PointApprovalManagementTax';
import PointApprovalManagementHistory from './pages/class/teacher/point-approval/PointApprovalManagementHistory';
import VoteManagement from './pages/class/teacher/VoteManagement';
import Vote from './pages/class/student/Vote';
import PointApprovalManagementOneTime from './pages/class/teacher/point-approval/PointApprovalManagementOneTime';
import SpecialPointApprovalManagement from './pages/class/teacher/SpecialPointApprovalManagement';
import { Editor as SpriteEditor } from './pages/makecodearcade/SpriteEditor';
import { CodeEditor } from './pages/makecodearcade/CodeEditor';
import Layout from './components/layout/Layout';
import Tools from './pages/tools/Tools';
import SubjectStudentHome from './pages/subject/student/SubjectStudentHome';
import SubjectStudentPage from './pages/subject/student/SubjectStudentPage';
import SubjectTeacherPage from './pages/subject/teacher/SubjectTeacherPage';
import SubjectTeacherHome from './pages/subject/teacher/SubjectTeacherHome';
import SubjectClassManagement from './pages/subject/teacher/SubjectClassManagement';
import SubjectClassStudent from './pages/subject/teacher/SubjectClassStudent';
import SubjectLessonManagement from './pages/subject/teacher/SubjectLessonManagement';
import SubjectSetting from './pages/subject/teacher/SubjectSetting';
import ClassTeacherHome from './pages/class/teacher/ClassTeacherHome';
import ToolsLayout from './components/ToolsLayout';
import NumberDrawer from './pages/tools/NumberDrawer';
import RouletteDrawer from './pages/tools/RouletteDrawer';
import MafiaGame from './pages/tools/MafiaGame';
import MafiaGameCreate from './pages/tools/MafiaGameCreate';
import MafiaGameJoin from './pages/tools/MafiaGameJoin';
import HandChakraStudio from './pages/tools/HandChakraStudio';
import CloudJumpGame from './pages/event/cloudJump/CloudJumpGame';
import AdminPage from './pages/admin/AdminPage';
import HomeroomTeacherManagement from './pages/admin/HomeroomTeacherManagement';
import SchoolRequestManagement from './pages/admin/SchoolRequestManagement';
import NotificationManagement from './pages/admin/NotificationManagement';
import SecurityManagement from './pages/admin/SecurityManagement';
import TeacherPlanManagement from './pages/admin/TeacherPlanManagement';
import AdminLayout from './components/AdminLayout';
import SubjectGames from './pages/SubjectGames';
import IBGrading from './pages/IBGrading/IBGrading';
import SubjectGroupGuide from './pages/IBGrading/SubjectGroupGuide';
import FinalGradeGuide from './pages/IBGrading/FinalGradeGuide';
import MinecraftEducationMain from './pages/minecraft-education/MinecraftEducationMain';
import MinecraftEducationUpload from './pages/minecraft-education/MinecraftEducationUpload';
import MinecraftEducationDetail from './pages/minecraft-education/MinecraftEducationDetail';
import MinecraftWorldShare from './pages/minecraft-education/MinecraftWorldShare';
import MinecraftModGuide from './pages/minecraft-education/MinecraftModGuide';
import MinecraftMakecodeShare from './pages/minecraft-education/MinecraftMakecodeShare';
import WorldShareTemplate from './pages/minecraft-education/templates/WorldShareTemplate';
import StudentClassroom from './pages/notes/student/StudentClassroom';
import StudentNotes from './pages/notes/student/StudentNotes';
import TeachingMaterials from './pages/notes/student/TeachingMaterials';
import AIRobotProject from './pages/notes/student/Microbit/AIRobotProject';
import PCA9685 from './pages/notes/student/Microbit/PCA9685';
import PoseLearningAI from './pages/notes/student/Microbit/PoseLearningAI';
import AIRobotControl from './pages/notes/student/Microbit/AIRobotControl';
import NotesManagement from './pages/notes/admin/NotesManagement';
import InfoChiefMainPage from './info-chief/InfoChiefMainPage';
import StudentProfile from './pages/class/student/StudentProfile';
import Profile from './pages/Profile';
import VirtualPetPage from './pages/class/student/VirtualPetPage';
import RoleAuction from './pages/class/student/RoleAuction';
import RoleAuctionManagement from './pages/class/teacher/RoleAuctionManagement';
import ManitoManagement from './pages/class/teacher/ManitoManagement';
import BirthdayManagement from './pages/class/teacher/BirthdayManagement';
import PublicBirthdayRollingPaper from './pages/class/public/PublicBirthdayRollingPaper';
import SeatingRoulette from './pages/class/teacher/seating-management/SeatingRoulette';

const DroneSimulatorLazy = lazy(() => import('./pages/tools/DroneSimulator'));

/** @deprecated 라우트는 SeatManagement(페이지 컴포넌트) 직접 사용. 캐시/레거시 참조 대응용 별칭 */
const SeatManagementWrapper = SeatManagement;

const SeatingRouletteWrapper: React.FC = () => {
  const { currentUser } = useAuth();
  if (!currentUser || !isTeacher(currentUser)) {
    return <Navigate to="/" replace />;
  }

  const { schoolId, homeroomClass } = currentUser;
  if (!schoolId || !homeroomClass?.grade || !homeroomClass?.class) {
    return <Navigate to="/" replace />;
  }

  const classId = `${schoolId}_${homeroomClass.grade}_${homeroomClass.class}`;
  return <SeatingRoulette schoolId={schoolId} classId={classId} />;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* 기본 라우트 - Layout + public 네비바 사용 */}
      <Route
        path="/"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="public">
            <Home />
          </Layout>
        }
      />
      
      {/* MARU 정책 페이지 (개인정보처리방침 등) */}
      <Route
        path="/policy"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="public">
            <MaruPolicy />
          </Layout>
        }
      />
      <Route
        path="/drone-simulator"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="public">
            <Suspense
              fallback={
                <div
                  style={{
                    minHeight: '50vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 24,
                    fontFamily: 'var(--font-family-primary)',
                    color: 'var(--Color-text-strong)',
                  }}
                >
                  드론 시뮬레이터를 불러오는 중…
                </div>
              }
            >
              <DroneSimulatorLazy />
            </Suspense>
          </Layout>
        }
      />

      <Route
        path="/event/cloud-jump"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="public">
            <CloudJumpGame />
          </Layout>
        }
      />

      {/* 공개 라우트 */}
      <Route
        path="/login"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="public">
            <Login />
          </Layout>
        }
      />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/signupchoice" element={<SignupChoice />} />
      <Route path="/signup" element={<SignupChoice />} />
      <Route path="/teacherregister" element={<TeacherRegister />} />
      <Route path="/studentregister" element={<Layout showNavbar={true} requireAuth={false} navbarType="public"><StudentRegister /></Layout>} />
      
      {/* 공개 프로필 라우트 */}
      <Route path="/public/teacher/:teacherId" element={<PublicTeacherProfile />} />

      <Route
        path="/class/public/birthday-rolling-paper/:paperId"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="public">
            <PublicBirthdayRollingPaper />
          </Layout>
        }
      />

      <Route path="/class/main/ClassManagementHome" element={<ClassManagementHome />} />
      <Route path="/ib-grading" element={<Layout showNavbar={true} requireAuth={false} navbarType="ib-grading"><IBGrading /></Layout>} />
      <Route path="/ib-grading/subject-group-guide" element={<Layout showNavbar={true} requireAuth={false} navbarType="ib-grading"><SubjectGroupGuide /></Layout>} />
      <Route path="/ib-grading/final-grade-guide" element={<Layout showNavbar={true} requireAuth={false} navbarType="ib-grading"><FinalGradeGuide /></Layout>} />
      {/* Minecraft Education 라우트 */}
      <Route path="/minecraft-education" element={<Layout showNavbar={true} requireAuth={false} navbarType="minecraft-education"><MinecraftEducationMain /></Layout>} />
      <Route path="/minecraft-education/world-share" element={<Layout showNavbar={true} requireAuth={false} navbarType="minecraft-education"><MinecraftWorldShare /></Layout>} />
      <Route path="/minecraft-education/mod-guide" element={<Layout showNavbar={true} requireAuth={false} navbarType="minecraft-education"><MinecraftModGuide /></Layout>} />
      <Route path="/minecraft-education/makecode" element={<Layout showNavbar={true} requireAuth={false} navbarType="minecraft-education"><MinecraftMakecodeShare /></Layout>} />
      <Route path="/minecraft-education/world-template/personal-activity" element={<Layout showNavbar={true} requireAuth={false} navbarType="minecraft-education"><WorldShareTemplate /></Layout>} />
      <Route path="/minecraft-education/upload" element={<Layout showNavbar={true} requireAuth={true} navbarType="minecraft-education"><MinecraftEducationUpload /></Layout>} />
      <Route path="/minecraft-education/:id" element={<Layout showNavbar={true} requireAuth={false} navbarType="minecraft-education"><MinecraftEducationDetail /></Layout>} />
      
      {/* 레거시 /gallery → MakeCode 에셋 팩 갤러리 */}
      <Route path="/gallery" element={<Navigate to="/makecodearcade/gallery" replace />} />
      <Route 
        path="/upload-image" 
        element={
          <ProtectedRoute>
            <UploadImage />
          </ProtectedRoute>
        } 
      />
      
      {/* 레거시 /notes 경로 리다이렉트 */}
      <Route path="/notes" element={<Navigate to={`${CLASS_MATERIALS_BASE}/materials`} replace />} />
      <Route
        path="/notes/student/*"
        element={
          <LegacyContentPathRedirect
            legacyPrefix="/notes/student"
            targetPrefix={CLASS_MATERIALS_BASE}
            defaultSuffix="/materials"
          />
        }
      />
      <Route
        path="/notes/teacher/*"
        element={
          <LegacyContentPathRedirect legacyPrefix="/notes/teacher" targetPrefix={TEACHER_TRAINING_BASE} />
        }
      />

      {/* 수업 자료 (로그인 불필요) */}
      <Route
        path={CLASS_MATERIALS_BASE}
        element={<Layout showNavbar={true} requireAuth={false} navbarType="notes-student" />}
      >
        <Route index element={<Navigate to={`${CLASS_MATERIALS_BASE}/materials`} replace />} />
        <Route path="classroom" element={<StudentClassroom />} />
        <Route path="notes" element={<StudentNotes />} />
        <Route path="materials" element={<TeachingMaterials />} />
        <Route path="ai-robot-project" element={<AIRobotProject />} />
        <Route path="Microbit/PCA9685" element={<PCA9685 />} />
        <Route path="Microbit/PoseLearningAI" element={<PoseLearningAI />} />
        <Route path="Microbit/AIRobotControl" element={<AIRobotControl />} />
      </Route>

      {/* 교사 연수 (로그인 불필요) */}
      <Route
        path={TEACHER_TRAINING_BASE}
        element={<Layout showNavbar={true} requireAuth={false} navbarType="notes-teacher" />}
      >
        <Route index element={<TrainingHostDirectory />} />
        <Route
          path="my"
          element={
            <ProtectedRoute allowedRoles={[...CONTENT_MANAGER_ROLES]}>
              <TrainingLobbyPage />
            </ProtectedRoute>
          }
        />
        <Route path="legacy" element={<TrainingMainPage />} />
        <Route path="h/:slug/p/:programSlug" element={<TrainingProgramPublicPage />} />
        <Route path="h/:slug" element={<TrainingHostPublicPage />} />
        <Route path="training" element={<Navigate to="/teacher-training/legacy" replace />} />
        <Route path="training-pages" element={<TrainingPages />} />
        <Route path="training-materials" element={<Navigate to="/teacher-training/training-pages" replace />} />
        <Route path="training/completed/MakeCodeArcadeTraining" element={<MakeCodeArcadeTraining />} />
        <Route path="training/upcoming/ThreeDModelingTraining" element={<ThreeDModelingTraining />} />
        <Route path="digital/DeaGwamgAIEdutechTraining" element={<DeaGwamgAIEdutechTraining />} />
        <Route path="digital/Elementary3DPrintingTraining2026" element={<Elementary3DPrintingTraining2026 />} />
        <Route path="digital/MiddleSchool3DPrintingTraining2026" element={<MiddleSchool3DPrintingTraining2026 />} />
        <Route path="digital/CentralMiddleDebethAIDigitalLeaderTraining2026" element={<CentralMiddleDebethAIDigitalLeaderTraining2026 />} />
        <Route path="digital/NamdaemoonMiddleSchoolDebethTraining2026" element={<NamdaemoonMiddleSchoolDebethTraining2026 />} />
        <Route path="digital/NamdaemoonMiddleSchoolDebethTraining2026Session2" element={<NamdaemoonMiddleSchoolDebethTraining2026Session2 />} />
        <Route path="digital/EwhaGeumranMiddleSchoolAITraining2026" element={<EwhaGeumranMiddleSchoolAITraining2026 />} />
        <Route path="digital/JingwanMiddleSchoolAITraining2026" element={<JingwanMiddleSchoolAITraining2026 />} />
        <Route path="AIEdutech" element={<AIEdutechMainpage />} />
        <Route path="AIEdutech/SetiVibeCodingTraining" element={<SetiVibeCodingTraining />} />
        <Route path="AIEdutech/RobotHighSchoolVibeCodingTraining" element={<RobotHighSchoolVibeCodingTraining />} />
        <Route path="AIEdutech/SunrinAITraining" element={<SunrinAITraining />} />
      </Route>

      {/* 수업 자료 — 교사 개인 관리 (로그인 필요) */}
      <Route
        path={CLASS_MATERIALS_MANAGE_BASE}
        element={
          <ProtectedRoute allowedRoles={[...CONTENT_MANAGER_ROLES]}>
            <Layout showNavbar={true} requireAuth={true} navbarType="class-materials-manage" />
          </ProtectedRoute>
        }
      >
        <Route index element={<ClassMaterialsManage />} />
      </Route>
      <Route
        path={`${CLASS_MATERIALS_BASE}/admin/*`}
        element={
          <LegacyContentPathRedirect
            legacyPrefix={`${CLASS_MATERIALS_BASE}/admin`}
            targetPrefix={CLASS_MATERIALS_MANAGE_BASE}
          />
        }
      />

      {/* 교사 연수 — 교사 개인 관리 (로그인 필요) */}
      <Route
        path={TEACHER_TRAINING_MANAGE_BASE}
        element={
          <ProtectedRoute allowedRoles={[...CONTENT_MANAGER_ROLES]}>
            <Layout showNavbar={true} requireAuth={true} navbarType="teacher-training-manage" />
          </ProtectedRoute>
        }
      >
        <Route index element={<TeacherTrainingManage />} />
        <Route path="host-profile" element={<Navigate to={TEACHER_TRAINING_LOBBY_PATH} replace />} />
        <Route path="programs/new/:mode" element={<TrainingProgramRegister />} />
        <Route path="programs/:programId/edit" element={<TrainingProgramEditor />} />
        <Route path="ai-edutech" element={<AIEdutechTrainingManage />} />
      </Route>
      <Route
        path={`${TEACHER_TRAINING_BASE}/admin/*`}
        element={<LegacyContentPathRedirect legacyPrefix={`${TEACHER_TRAINING_BASE}/admin`} targetPrefix={TEACHER_TRAINING_MANAGE_BASE} />}
      />

      {/* 정보부장 전용 라우트 (로그인 불필요) */}
      <Route
        path="/info-chief"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="info-chief">
            <InfoChiefMainPage />
          </Layout>
        }
      />
      <Route
        path="/info-chief/info-category"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="info-chief">
            <InfoChiefMainPage />
          </Layout>
        }
      />
      <Route
        path="/info-chief/privacy-category"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="info-chief">
            <InfoChiefMainPage />
          </Layout>
        }
      />
      <Route
        path="/info-chief/privacy-internal-management-plan"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="info-chief">
            <InfoChiefMainPage />
          </Layout>
        }
      />
      <Route
        path="/info-chief/cyber-security-day"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="info-chief">
            <InfoChiefMainPage />
          </Layout>
        }
      />
      <Route
        path="/info-chief/security-officer-designation"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="info-chief">
            <InfoChiefMainPage />
          </Layout>
        }
      />
      <Route
        path="/info-chief/info-security-education"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="info-chief">
            <InfoChiefMainPage />
          </Layout>
        }
      />
      <Route
        path="/info-chief/network-security"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="info-chief">
            <InfoChiefMainPage />
          </Layout>
        }
      />
      <Route
        path="/info-chief/information-system-security"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="info-chief">
            <InfoChiefMainPage />
          </Layout>
        }
      />
      <Route
        path="/info-chief/terminal-device-security"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="info-chief">
            <InfoChiefMainPage />
          </Layout>
        }
      />
      <Route
        path="/info-chief/controlled-area-management"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="info-chief">
            <InfoChiefMainPage />
          </Layout>
        }
      />
      <Route
        path="/info-chief/portable-storage-media"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="info-chief">
            <InfoChiefMainPage />
          </Layout>
        }
      />
      <Route
        path="/info-chief/it-project-vendor-management"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="info-chief">
            <InfoChiefMainPage />
          </Layout>
        }
      />
      <Route
        path="/info-chief/cyber-incident-reporting"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="info-chief">
            <InfoChiefMainPage />
          </Layout>
        }
      />
      <Route
        path="/info-chief/device-materials"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="info-chief">
            <InfoChiefMainPage />
          </Layout>
        }
      />
      <Route
        path="/info-chief/url-access-list"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="info-chief">
            <InfoChiefMainPage />
          </Layout>
        }
      />
      <Route
        path="/info-chief/related-sites"
        element={
          <Layout showNavbar={true} requireAuth={false} navbarType="info-chief">
            <InfoChiefMainPage />
          </Layout>
        }
      />

      {/* 레거시 통합 관리 허브 → 분리된 관리 페이지로 안내 */}
      <Route
        path="/notes/admin"
        element={
          <ProtectedRoute allowedRoles={[...CONTENT_MANAGER_ROLES]}>
            <Layout showNavbar={true} requireAuth={true} navbarType="admin" />
          </ProtectedRoute>
        }
      >
        <Route index element={<NotesManagement />} />
        <Route path="management" element={<NotesManagement />} />
      </Route>

      {/* MakeCode Arcade 관련 라우트 */}
      <Route path="/makecodearcade" element={<Layout />}>
        <Route index element={<ArcadeMain />} />
        <Route path="ArcadeMain" element={<Navigate to="/makecodearcade" replace />} />
        <Route path="store" element={<Store />} />
        <Route path="game/:gameId" element={<GameDetail />} />
        <Route path="chart" element={<GameChart />} />
        <Route path="explains" element={<Explains />} />
        <Route path="explains/tilemap" element={<TilemapExplain />} />
        <Route path="explains/textsprite" element={<TextSpriteExplain />} />
        <Route path="explains/Story" element={<StoryExplain />} />
        <Route path="explains/ScreenScroll" element={<ScreenScrollExplain />} />
        <Route path="music-share" element={<Navigate to="/makecodearcade/extensions/music" replace />} />
        <Route path="extensions" element={<Extensions />} />
        <Route path="extensions/code-examples" element={<AssetPackShare listing="codeExample" />} />
        <Route path="extensions/sample-assignments" element={<Navigate to="/makecodearcade/extensions/code-examples" replace />} />
        <Route path="extensions/imageconverter" element={<ImageConverter />} />
        <Route path="extensions/image-converter" element={<ImageConverter />} />
        <Route path="extensions/music" element={<AssetPackShare listing="music" />} />
        <Route path="extensions/sharingMusic" element={<Navigate to="/makecodearcade/extensions/music" replace />} />
        <Route path="extensions/tilemap" element={<TilemapExtension />} />
        <Route path="extensions/textsprite" element={<TextSpriteExtension />} />
        <Route path="gallery" element={<AssetPackShare />} />
        <Route path="sprite-gallery" element={<ImageGallery />} />
        <Route path="asset-packs" element={<Navigate to="/makecodearcade/gallery" replace />} />
        <Route path="evaluation-code" element={<EvaluationCodeGenerator />} />
        <Route path="upload-code" element={<UploadCodePage />} />
        <Route path="register-guide" element={<RegisterGuide />} />
        <Route path="register/*" element={<GameRegister />} />
        <Route 
          path="upload-image" 
          element={
            <ProtectedRoute>
              <UploadImage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="game-management" 
          element={
            <ProtectedRoute allowedRoles={['teacher', 'schoolAdmin', 'superAdmin']}>
              <GameClassManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="student-management" 
          element={
            <ProtectedRoute allowedRoles={['teacher', 'schoolAdmin', 'superAdmin']}>
              <GameStudentManagement />
            </ProtectedRoute>
          } 
        />
        <Route path="editor" element={<SpriteEditor />} />
        <Route path="editor/code" element={<CodeEditor />} />
      </Route>
      
      {/* 보호된 라우트들 */}
      <Route path="/class" element={<ProtectedRoute><Layout showNavbar={true} requireAuth={true} /></ProtectedRoute>}>
        <Route index element={<Navigate to="/class/student" replace />} />
        <Route path="student" element={<ClassStudentHome />} />
        <Route path="student/StudentPage" element={<Navigate to="/class/student" replace />} />
        <Route path="student/ClassStudentHome" element={<ClassStudentHome />} />
        <Route path="student/ItemShop" element={<Navigate to="/class/student/ClassItemShop" replace />} />
        <Route path="student/ClassItemShop" element={<ClassItemShop />} />
        {FEATURE_CHARACTER_COSMETIC && (
          <Route
            path="student/CharacterWardrobe"
            element={
              <Suspense fallback={<div style={{ padding: 24 }}>로딩 중…</div>}>
                <CharacterWardrobeLazy />
              </Suspense>
            }
          />
        )}
        <Route path="student/MyItems" element={<Navigate to="/class/student/ClassMyItems" replace />} />
        <Route path="student/ClassMyItems" element={<ClassMyItems />} />
        <Route path="student/StudentClassPoints" element={<StudentClassPoints />} />
        <Route path="student/StudentAchievements" element={<StudentAchievements />} />
        <Route path="student/StudentStamp" element={<StudentStamp />} />
        <Route
          path="student/SeatReservation"
          element={
            <PlatformProtectedRoute feature="SEATING" redirectTo="/class/student">
              <SeatReservation />
            </PlatformProtectedRoute>
          }
        />
        <Route path="student/StudentSettings" element={<StudentSettings />} />
        <Route path="student/MessageInbox" element={<MessageInbox />} />
        <Route path="student/StudentPointApproval" element={<StudentPointApproval />} />
        <Route path="student/point-approval" element={<StudentPointApproval />} />
        <Route path="student/Vote" element={<Vote />} />
        <Route path="student/RoleAuction" element={<RoleAuction />} />
        <Route path="student/VirtualPet" element={<VirtualPetPage />} />
        <Route path="student/MyHome" element={<StudentProfile />} />
        <Route path="student/profile" element={<Navigate to="/class/student/StudentSettings" replace />} />
        <Route path="student/Manito" element={<StudentManito />} />
        <Route path="student/BirthdayRollingPaper" element={<StudentBirthdayRollingPaper />} />
      </Route>

      <Route
        path="/class/kiosk/connect"
        element={
          <PlatformProtectedRoute feature="SEATING" redirectTo="/class/teacher">
            <Layout showNavbar={false} requireAuth={false}>
              <KioskConnect />
            </Layout>
          </PlatformProtectedRoute>
        }
      />

      <Route
        path="/class/teacher/SeatDisplay"
        element={
          <PlatformProtectedRoute feature="SEATING" redirectTo="/class/teacher">
            <Layout showNavbar={false} requireAuth={false}>
              <SeatDisplay />
            </Layout>
          </PlatformProtectedRoute>
        }
      />

      <Route path="/class/teacher" element={<ProtectedRoute><Layout showNavbar={true} requireAuth={true} /></ProtectedRoute>}>
        <Route index element={<ClassTeacherHome />} />
        <Route path="ClassTeacherHome" element={<ClassTeacherHome />} />
        <Route path="ClassManagement" element={<ClassManagement />} />
        <Route
          path="ClassMaterials"
          element={
            <PlatformProtectedRoute feature="CLASS_MATERIALS" redirectTo="/class/teacher">
              <ClassMaterials />
            </PlatformProtectedRoute>
          }
        />
        <Route path="StoreManagement" element={<StoreManagement />} />
        <Route path="ScheduleManagement" element={<ScheduleManagement />} />
        <Route
          path="SeatManagement"
          element={
            <PlatformProtectedRoute feature="SEATING" redirectTo="/class/teacher">
              <SeatManagement />
            </PlatformProtectedRoute>
          }
        />
        <Route path="AttendanceManagement" element={<AttendanceManagement />} />
        <Route path="VirtualPetManagement" element={<VirtualPetManagement />} />
        <Route path="MyHomeModeration" element={<MyHomeModeration />} />
        <Route
          path="seating-management/SeatingRoulette"
          element={
            <PlatformProtectedRoute feature="SEATING" redirectTo="/class/teacher">
              <SeatingRouletteWrapper />
            </PlatformProtectedRoute>
          }
        />
        <Route path="TeacherSetting" element={<TeacherSetting />} />
        <Route path="MessageInbox" element={<MessageInbox />} />
        <Route path="ClassRoleManagement" element={<ClassRoleManagement />} />
        <Route path="RoleAuctionManagement" element={<RoleAuctionManagement />} />
        <Route path="ManitoManagement" element={<ManitoManagement />} />
        <Route path="BirthdayManagement" element={<BirthdayManagement />} />
        <Route path="PointApprovalManagement" element={<PointApprovalManagement />} />
        <Route path="ClassPointManagement" element={<ClassPointManagement />} />
        <Route path="StampPage" element={<StampPage />} />
        <Route path="NumberDraw" element={<NumberDraw />} />
        <Route path="Lottery" element={<Lottery />} />
        <Route path="TeacherProfile" element={<TeacherProfile />} />
        <Route path="profile" element={<TeacherProfile />} />
        <Route path="ItemScanner" element={<ItemScanner />} />
        <Route path="ItemApproval" element={<ItemApproval />} />
        <Route path="ItemApprovalManagement" element={<Navigate to="/class/teacher/ItemApproval" replace />} />
        <Route path="EventManagement" element={<EventManagement />} />
        <Route path="AchievementManagement" element={<AchievementManagement />} />
        <Route
          path="SeatReservationManagement"
          element={
            <PlatformProtectedRoute feature="SEATING" redirectTo="/class/teacher">
              <SeatReservationManagement />
            </PlatformProtectedRoute>
          }
        />
        <Route path="VoteManagement" element={<VoteManagement />} />
        <Route path="WordGame" element={<WordGame />} />
        <Route path="point-approval/daily" element={<PointApprovalManagementDaily />} />
        <Route path="point-approval/weekly" element={<PointApprovalManagementWeekly />} />
        <Route path="point-approval/monthly" element={<PointApprovalManagementMonthly />} />
        <Route path="point-approval/tax" element={<PointApprovalManagementTax />} />
        <Route path="point-approval/history" element={<PointApprovalManagementHistory />} />
        <Route path="point-approval/onetime" element={<PointApprovalManagementOneTime />} />
        <Route path="special-point-approval" element={<SpecialPointApprovalManagement />} />
      </Route>

      {/* 관리자 전용 라우트 */}
      <Route 
        path="/class/admin" 
        element={
          <ProtectedRoute allowedRoles={['superAdmin', 'schoolAdmin']}>
            <Layout showNavbar={true} requireAuth={true} />
          </ProtectedRoute>
        } 
      >
        <Route path="SchoolManagement" element={<SchoolManagement />} />
        <Route path="teacher-approval" element={<TeacherApproval />} />
      </Route>

      <Route path="/subject" element={<ProtectedRoute><Layout showNavbar={true} requireAuth={true} /></ProtectedRoute>}>
        <Route index element={<Navigate to="/subject/teacher/SubjectTeacherHome" replace />} />
        <Route path="student" element={<SubjectStudentHome />} />
        <Route path="student/StudentPage" element={<SubjectStudentPage />} />
        <Route path="teacher" element={<Navigate to="/subject/teacher/SubjectTeacherHome" replace />} />
        <Route path="teacher/SubjectTeacherHome" element={<SubjectTeacherHome />} />
        <Route path="teacher/SubjectTeacherPage" element={<SubjectTeacherPage />} />
        <Route path="teacher/ClassManagement" element={<SubjectClassManagement />} />
        <Route path="teacher/class/:grade/:class" element={<SubjectClassStudent />} />
        <Route path="teacher/LessonManagement" element={<SubjectLessonManagement />} />
        <Route path="teacher/settings" element={<SubjectSetting />} />
        <Route path="teacher/stamp" element={<StampPage />} />
        <Route path="teacher/VoteManagement" element={<div style={{ padding: '20px', textAlign: 'center' }}>투표 관리 페이지 (준비 중)</div>} />
        <Route path="teacher/AchievementManagement" element={<AchievementManagement />} />
      </Route>

      {/* Tools 관련 라우트 */}
      <Route path="/tools" element={<ToolsLayout />}>
        <Route index element={<Tools />} />
        <Route path="number-draw" element={<NumberDrawer />} />
        <Route path="roulette" element={<RouletteDrawer />} />
        <Route path="mafia-game" element={<MafiaGame />} />
        <Route path="mafia-game/create" element={<MafiaGameCreate />} />
        <Route path="mafia-game/join" element={<MafiaGameJoin />} />
        <Route path="mafia-game/join/:code" element={<MafiaGameJoin />} />
        <Route
          path="drone-simulator"
          element={
            <Suspense
              fallback={
                <div
                  style={{
                    minHeight: 'calc(100vh - 56px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 24,
                    fontFamily: 'var(--font-family-primary)',
                    color: 'var(--Color-text-strong)',
                    background: 'var(--Color-background-main, #F4F6FA)',
                  }}
                >
                  드론 시뮬레이터를 불러오는 중…
                </div>
              }
            >
              <DroneSimulatorLazy />
            </Suspense>
          }
        />
        <Route path="hand-chakra" element={<HandChakraStudio />} />
      </Route>

      {/* 관리자 라우트 */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute allowedRoles={['superAdmin', 'schoolAdmin']}>
            <AdminLayout />
          </ProtectedRoute>
        } 
      >
        <Route index element={<AdminPage />} />
        <Route path="school-requests" element={<SchoolRequestManagement />} />
        <Route path="school-management" element={<SchoolManagement />} />
        <Route path="homeroom-teachers" element={<HomeroomTeacherManagement />} />
        <Route path="teacher-approval" element={<TeacherApproval />} />
        <Route path="notifications" element={<NotificationManagement />} />
        <Route path="security" element={<SecurityManagement />} />
        <Route path="teacher-plans" element={<TeacherPlanManagement />} />
      </Route>
    </Routes>
  );
};

// 페이지 전환 시 스크롤을 맨 위로 이동하는 컴포넌트
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // 즉시 스크롤 초기화 함수
    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      // 모든 스크롤 가능한 컨테이너 찾아서 초기화
      const scrollableElements = document.querySelectorAll('[style*="overflow"], [style*="overflow-y"]');
      scrollableElements.forEach((el) => {
        if (el instanceof HTMLElement) {
          el.scrollTop = 0;
        }
      });
    };
    
    // 즉시 실행
    scrollToTop();
    
    // requestAnimationFrame으로 다음 프레임에서도 실행
    const rafId1 = requestAnimationFrame(() => {
      scrollToTop();
      
      // 추가로 여러 시점에서 재시도
      const rafId2 = requestAnimationFrame(() => {
        scrollToTop();
        
        // 짧은 지연 후 재시도
        setTimeout(() => {
          scrollToTop();
        }, 0);
        
        // 더 긴 지연 후 재시도 (컴포넌트 마운트 완료 후)
        setTimeout(() => {
          scrollToTop();
        }, 10);
        
        // 최종 확인 (모든 렌더링 완료 후)
        setTimeout(() => {
          scrollToTop();
        }, 50);
      });
      
      return () => cancelAnimationFrame(rafId2);
    });
    
    return () => {
      cancelAnimationFrame(rafId1);
    };
  }, [pathname]);

  return null;
};

const App: React.FC = () => {
  // Microsoft Teams 초기화
  useEffect(() => {
    initializeTeams().then((context) => {
      if (context.isInTeams) {
        console.log('Microsoft Teams 환경에서 실행 중');
        // Teams 환경에 맞는 스타일 조정 (선택적)
        document.body.style.padding = '0';
        document.body.style.margin = '0';
      }
    }).catch((error) => {
      console.error('Teams 초기화 중 오류:', error);
    });
  }, []);

  return (
    <AuthProvider>
      <ScrollToTop />
      <AppRoutes />
    </AuthProvider>
  );
};

export default App;
